import { ChangeEvent, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActiveConnectionAction } from "redux/action/activeConnectionsActions";
import { qbdItemsAction } from "redux/action/itemsAction";
import { AppDispatch } from "redux/store";
import DynamicTable from "./Table";
import NewCompany from "./NoCompany/newCompany";
import { useNavigate } from "react-router-dom";
import { getApi, putApi } from "redux/apis";
import { toastText } from "utils/utils";
import Cookies from "js-cookie";
import IncomingInventoryUploadModal from "components/IncomingInventoryUploadModal";
import IncomingDetailModal from "components/IncomingDetailModal";
import SiteQtyReviewModal from "components/SiteQtyReviewModal";

const Home = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // Redux state
  const { items: ItemDataState, totalRecords } = useSelector(
    (state: any) => state?.qbdItems?.data || {}
  );

  const allActiveConnection = useSelector(
    (state: any) => state?.ActiveServices?.data
  );

  const [PageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemDataSource, setItemDataSource] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchFields, setSearchFields] = useState<string[]>([
    "name",
    "salesDescription",
  ]);

  const [itemModelOptions, setItemModelOptions] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState<
    "ALL" | "AVAILABLE" | "LIMITED" | "CHECK"
  >("ALL");

  // Handlers
  const paginationChangeHandler = (pageNo: number, pageSize: number) => {
    setCurrentPage(pageNo);
    setPageSize(pageSize);
  };

  const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchValue(value);
    setCurrentPage(1);
  };

  const resetButtonHandler = () => {
    setSearchValue("");
    setSearchFields(["name", "salesDescription"]);
    setCurrentPage(1);
    setAvailabilityFilter("ALL");
  };

  const searchFieldsSelectHandler = (value: string[]) => {
    setSearchFields(value);
    setCurrentPage(1);
  };
  const availabilityFilterChangeHandler = (
    value: "ALL" | "AVAILABLE" | "LIMITED" | "CHECK"
  ) => {
    setAvailabilityFilter(value);
    setCurrentPage(1);
  };
  const profileData = useSelector((state: any) => state?.userProfile?.data);
  const roleName = profileData?.companyRoles[0]?.role?.roleName;
  const viewMode = (profileData?.customerInventoryViewMode || "COMMERCIAL")
    .toString()
    .toUpperCase();
  const isCustomerCommercial = roleName === "Customer" && viewMode === "COMMERCIAL";
  const accessToken = Cookies.get("accessToken");

  const fetchItemModelOptions = async () => {
    if (!accessToken) return;
    if (!(roleName === "Admin 3nStar" || roleName === "3nStar Employee 1")) {
      return;
    }
    try {
      const response = await getApi("/itemModel/model-options");
      setItemModelOptions(response?.data?.data?.data);
    } catch (error: any) {
      toastText(
        error.response?.data?.error?.message ||
          "Something went wrong in fetching item models",
        "error"
      );
    }
  };

  const handleModelChange = async (value: any, record: any) => {
    try {
      const response = await putApi("/items/update-model", {
        id: record.id,
        modelId: value,
      });
      setItemDataSource((prev) =>
        prev.map((item) => {
          if (item.id === record.id) {
            return { ...item, modelId: value };
          }
          return item;
        })
      );
      const message = response?.data?.message || "Model updated successfully";
      toastText(message, "success");
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message ||
        "Something went wrong while updating model";
      toastText(message, "error");
    }
  };

  useEffect(() => {
    if (roleName == "Service Center") {
      navigate("/inventory-control");
    } else if (roleName == "Admin 3nStar") {
      navigate("");
    }
  }, []);
  // Fetch data from Redux action
  useEffect(() => {
    if (!accessToken) {
      return;
    }
    const query = {
      pageSize: PageSize,
      pageNumber: currentPage,
      fields: searchFields,
      searchValue: searchValue?.trim(),
      availabilityFilter: isCustomerCommercial ? availabilityFilter : "ALL",
    };

    const fetchData = async () => {
      try {
        await dispatch(getActiveConnectionAction());
        await dispatch(qbdItemsAction(query));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData(); // Fetch data on component mount
  }, [
    dispatch,
    PageSize,
    currentPage,
    searchFields,
    searchValue,
    availabilityFilter,
    isCustomerCommercial,
    accessToken,
  ]);

  // Update itemDataSource whenever ItemDataState changes
  useEffect(() => {
    if (ItemDataState) {
      setItemDataSource(ItemDataState);
    }
  }, [ItemDataState]);

  useEffect(() => {
    fetchItemModelOptions();
  }, [roleName, accessToken]);

  // Incoming inventory state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [reviewQtyModalOpen, setReviewQtyModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailSku, setDetailSku] = useState("");

  const handleUploadIncoming = useCallback(() => {
    setUploadModalOpen(true);
  }, []);

  const handleReviewSiteQty = useCallback(() => {
    setReviewQtyModalOpen(true);
  }, []);

  const handleRefreshIncoming = useCallback(() => {
    const query = {
      pageSize: PageSize,
      pageNumber: currentPage,
      fields: searchFields,
      searchValue: searchValue?.trim(),
      availabilityFilter: isCustomerCommercial ? availabilityFilter : "ALL",
    };
    dispatch(qbdItemsAction(query));
  }, [dispatch, PageSize, currentPage, searchFields, searchValue, isCustomerCommercial, availabilityFilter]);

  const handleIncomingClick = useCallback((sku: string) => {
    setDetailSku(sku);
    setDetailModalOpen(true);
  }, []);

  const handleUploadSuccess = useCallback(() => {
    handleRefreshIncoming();
  }, [handleRefreshIncoming]);

  return (
    <>
      <>
        {allActiveConnection &&
        allActiveConnection.length > 0 &&
        allActiveConnection[0].isActiveConnection ? (
          <div>
            <p
              style={{
                margin: "10px 0px",
                fontSize: "16px",
              }}
            >
              Item Inventory data for :&nbsp;
              <span
                style={{
                  fontWeight: "bolder",
                }}
              >
                {allActiveConnection &&
                  allActiveConnection.length > 0 &&
                  allActiveConnection[0].companyName}
              </span>
            </p>
            <DynamicTable
              itemDataSource={itemDataSource}
              paginationChangeHandler={paginationChangeHandler}
              PageSize={PageSize}
              currentPage={currentPage}
              totalRecords={totalRecords}
              performSearchHandler={performSearchHandler}
              searchValue={searchValue}
              searchFieldsSelectHandler={searchFieldsSelectHandler}
              resetButtonHandler={resetButtonHandler}
              searchFields={searchFields}
              itemModelOptions={itemModelOptions}
              handleModelChange={handleModelChange}
              roleName={roleName}
              isCustomerCommercial={isCustomerCommercial}
              availabilityFilter={availabilityFilter}
              availabilityFilterChangeHandler={availabilityFilterChangeHandler}
              onIncomingClick={handleIncomingClick}
              handleUploadIncoming={handleUploadIncoming}
              handleReviewSiteQty={handleReviewSiteQty}
            />
          </div>
        ) : (
          <NewCompany />
        )}
      </>

      <IncomingInventoryUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />

      <SiteQtyReviewModal
        isOpen={reviewQtyModalOpen}
        onClose={() => setReviewQtyModalOpen(false)}
      />

      <IncomingDetailModal
        isOpen={detailModalOpen}
        sku={detailSku}
        onClose={() => setDetailModalOpen(false)}
      />
    </>
  );
};

export default Home;
